import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import {
  getAccessToken,
  getCurrentUser,
  getDevAuthUser,
  initKeycloak,
  isDevAuthDisabled,
  login,
  logout,
} from "./keycloak";
import type { AuthUser } from "./keycloak";

type AuthStatus = "loading" | "anonymous" | "authenticated" | "error";

export type AuthContextValue = {
  status: AuthStatus;
  isAuthenticated: boolean;
  user: AuthUser | null;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function initializeAuth() {
      try {
        if (isDevAuthDisabled()) {
          if (!mounted) return;
          setUser(getDevAuthUser());
          setStatus("authenticated");
          return;
        }

        const authenticated = await initKeycloak();
        if (!mounted) return;

        if (!authenticated) {
          setUser(null);
          setStatus("anonymous");
          return;
        }

        setUser(getCurrentUser());
        setStatus("authenticated");
      } catch (authError) {
        if (!mounted) return;
        setError(authError instanceof Error ? authError.message : "Erreur d'authentification.");
        setStatus("error");
      }
    }

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const handleLogin = useCallback(async () => {
    await login();
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      isAuthenticated: status === "authenticated",
      user,
      error,
      login: handleLogin,
      logout: handleLogout,
      getAccessToken,
    }),
    [error, handleLogin, handleLogout, status, user],
  );

  if (status === "loading") {
    return <AuthShell title="Chargement de la session" />;
  }

  if (status === "error") {
    return <AuthShell title="Authentification indisponible" detail={error ?? "Erreur inconnue."} />;
  }

  if (status === "anonymous") {
    return <AuthShell title="Connexion requise" actionLabel="Se connecter" onAction={handleLogin} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function AuthShell(props: { title: string; detail?: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-live="polite">
        <h1>{props.title}</h1>
        {props.detail ? <p>{props.detail}</p> : null}
        {props.actionLabel && props.onAction ? (
          <button type="button" onClick={props.onAction}>
            {props.actionLabel}
          </button>
        ) : null}
      </section>
    </main>
  );
}
